import { useEffect, useState } from "react";

/* useWeatherBoard — asks Open-Meteo for current conditions at (lat, lng) and,
   if the weather is decisive, returns a banner key to swap the TopBoard to:
     • "monsoon" — heavy rain / thunderstorm
     • "clear"   — cloudless bright sky, low precipitation
     • null      — inconclusive (keep whatever the picker chose)

   Open-Meteo is free, keyless and CORS-enabled. Results cached in
   sessionStorage for 30 minutes per airport to avoid refetch spam. */

const TTL_MS = 30 * 60 * 1000;
const KEY = (lat, lng) => `wxb:${lat.toFixed(2)}:${lng.toFixed(2)}`;

async function fetchWeatherKey(lat, lng) {
  const cached = sessionStorage.getItem(KEY(lat, lng));
  if (cached) {
    try {
      const { at, key } = JSON.parse(cached);
      if (Date.now() - at < TTL_MS) return key;
    } catch { /* fall through */ }
  }
  const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}` +
    `&current=precipitation,weather_code,cloud_cover&timezone=UTC`;
  const r = await fetch(url);
  if (!r.ok) throw new Error("weather fetch failed");
  const data = await r.json();
  const p = Number(data?.current?.precipitation ?? 0);
  const wc = Number(data?.current?.weather_code ?? -1);
  const cc = Number(data?.current?.cloud_cover ?? 100);
  // WMO codes: 61–65 rain, 66–67 freezing rain, 80–82 rain showers, 95–99 thunderstorm
  const heavyRain = p >= 2 || (wc >= 61 && wc <= 67) || (wc >= 80 && wc <= 82) || (wc >= 95 && wc <= 99);
  const clearSky = wc === 0 && cc < 25;
  const key = heavyRain ? "monsoon" : clearSky ? "clear" : null;
  try { sessionStorage.setItem(KEY(lat, lng), JSON.stringify({ at: Date.now(), key })); } catch { /* quota */ }
  return key;
}

export default function useWeatherBoard(entity) {
  const [key, setKey] = useState(null);
  useEffect(() => {
    setKey(null);
    const lat = Number(entity?.latitude_deg ?? entity?.latitude ?? entity?.lat);
    const lng = Number(entity?.longitude_deg ?? entity?.longitude ?? entity?.lng ?? entity?.lon);
    if (!isFinite(lat) || !isFinite(lng) || (lat === 0 && lng === 0)) return;
    let cancelled = false;
    fetchWeatherKey(lat, lng).then((k) => { if (!cancelled) setKey(k); }).catch(() => {});
    return () => { cancelled = true; };
  }, [entity?.latitude_deg, entity?.longitude_deg, entity?.latitude, entity?.longitude, entity?.lat, entity?.lng, entity?.lon]);
  return key;
}
